import { Box, VStack, HStack, Text, Badge, Menu, MenuButton, MenuList, MenuItem, IconButton, MenuDivider } from "@chakra-ui/react";
import { StatusBadge, CurrencyText } from "../../../shared/ui";
import { Pencil, RotateCcw, Ban, MoreVertical, Check, Send, Star, Copy } from "../../../shared/icons";
import { SCHEDULE_STATUS_META } from "../../plantoes/utils/statusPlantao";
import VinculoColorDot from "../../vinculos/components/VinculoColorDot";
import { DirectionsMenuItems } from "../../vinculos/components/DirectionsMenu";
import { describeShiftTime } from "../utils/shiftTime";

// "19:00 → 07:00 (+1) · 12h", ou só "12h" sem hora de início.
export function shiftTimeLine(shift) {
  const time = describeShiftTime(shift);
  const hours = shift.horas ? `${shift.horas}h` : "";
  return [time, hours].filter(Boolean).join(" · ");
}

// Nome do botão de ações: com vínculo e data, para que cada linha seja distinguível por
// leitor de tela ("Ações do plantão em Hospital São Lucas no dia 10/11/2026").
function shiftActionsLabel(shift) {
  return [
    "Ações do plantão",
    shift.vinculo_nome ? `em ${shift.vinculo_nome}` : "",
    shift.data ? `no dia ${shift.data}` : "",
  ]
    .filter(Boolean)
    .join(" ");
}

// Menu de ações de um plantão — o mesmo no popover do dia (Mês), na Semana e na Lista.
// `actions`: onEdit, onReschedule, onCancel, onInvoiceIssued, onReceived e, opcional,
// onDuplicate. O "Como chegar" aparece quando o vínculo tem endereço.
export function ShiftActionsMenu({ shift, vinculo, actions }) {
  return (
    <Menu placement="bottom-end" isLazy>
      <MenuButton as={IconButton} aria-label={shiftActionsLabel(shift)} icon={<MoreVertical size={14} />} size="xs" variant="ghost" />
      <MenuList>
        <MenuItem icon={<Pencil size={14} />} onClick={() => actions.onEdit(shift)}>
          Editar
        </MenuItem>
        <MenuItem icon={<RotateCcw size={14} />} onClick={() => actions.onReschedule(shift)}>
          Reagendar
        </MenuItem>
        {actions.onDuplicate ? (
          <MenuItem icon={<Copy size={14} />} onClick={() => actions.onDuplicate(shift)}>
            Duplicar
          </MenuItem>
        ) : null}
        {shift.estado === "aguardando_nf" ? (
          <MenuItem icon={<Send size={14} />} onClick={() => actions.onInvoiceIssued(shift)}>
            Marcar NF emitida
          </MenuItem>
        ) : null}
        {shift.estado !== "recebido" ? (
          <MenuItem icon={<Check size={14} />} onClick={() => actions.onReceived(shift)}>
            Marcar como recebido
          </MenuItem>
        ) : null}
        <MenuItem icon={<Ban size={14} />} color="danger-text" onClick={() => actions.onCancel(shift)}>
          Cancelar
        </MenuItem>
        {vinculo?.endereco ? (
          <>
            <MenuDivider />
            <DirectionsMenuItems endereco={vinculo.endereco} />
          </>
        ) : null}
      </MenuList>
    </Menu>
  );
}

// Um plantão com cor do vínculo, importante, horário (com "(+1)" quando vira a
// meia-noite), observação, status, valor e o menu de ações. `compact` empilha o valor
// sob os detalhes, para as colunas estreitas da Semana.
export default function ShiftRow({ shift, vinculo, actions, compact = false }) {
  const timeLine = shiftTimeLine(shift);
  const value = (
    <Text fontSize="sm" fontWeight="semibold" whiteSpace="nowrap">
      <CurrencyText value={shift.valor} />
    </Text>
  );

  return (
    <HStack justify="space-between" align="flex-start" spacing={1} data-testid="shift-row">
      <VStack align="flex-start" spacing={1} minW={0}>
        <HStack spacing={1} minW={0}>
          {vinculo?.cor ? <VinculoColorDot cor={vinculo.cor} size="8px" /> : null}
          {shift.importante ? (
            <Box as="span" color="warning.500" display="inline-flex" role="img" aria-label="Importante">
              <Star size={12} fill="currentColor" aria-hidden />
            </Box>
          ) : null}
          <Text fontSize="sm" fontWeight="medium" noOfLines={compact ? 2 : 1}>
            {shift.vinculo_nome}
          </Text>
        </HStack>
        {timeLine ? (
          <Text fontSize="xs" color="app-muted-fg">
            {timeLine}
          </Text>
        ) : null}
        {shift.observacao ? (
          <Text fontSize="xs" color="app-muted-fg" noOfLines={2} title={shift.observacao}>
            {shift.observacao}
          </Text>
        ) : null}
        {compact ? value : null}
        <HStack spacing={1} flexWrap="wrap">
          <StatusBadge status={shift.status} />
          {shift.schedule_status === "scheduled" ? (
            <Badge variant="statusSubtle" colorScheme={SCHEDULE_STATUS_META.scheduled.colorScheme}>
              {SCHEDULE_STATUS_META.scheduled.label}
            </Badge>
          ) : null}
        </HStack>
      </VStack>
      <HStack spacing={1} flexShrink={0}>
        {compact ? null : value}
        <ShiftActionsMenu shift={shift} vinculo={vinculo} actions={actions} />
      </HStack>
    </HStack>
  );
}
